import React, { useState } from 'react';
import { toast } from 'sonner';
import { studentApi } from '../../pages/journey/journeyApi';
import { ArrowRight, BellRing, CheckCircle2, Clock3, Compass } from 'lucide-react';
import { Card, Pill, ScoreBar, SectionHeader } from './ui';
import { DIMENSIONS, PROGRAM_NAMES, STATE_META } from '../../pages/journey/journeyApi';
import { TargetRolePicker } from './TargetRolePicker';

const STAGES = (d) => [
  { t: 'Talent Network registration', done: true },
  { t: 'Career Readiness Check (optional)', done: !d.readiness_link },
  { t: 'Career Clarity Consultation (₹499)', done: d.consultation.status === 'completed', note: d.consultation.status === 'booked' ? 'Booked · paid' : null },
  { t: 'Personalized Career Summary approved', done: !!d.summary },
  { t: 'Program enrollment', done: d.entitlements.length > 0 },
  { t: 'Practical learning & weekly check-ins', done: d.progress_history.some((x) => ['approved', 'validated'].includes(x.status)) || d.checkins.some((c) => c.status === 'reviewed') },
];

const Profile = ({ s }) => (
  <Card data-testid="overview-profile">
    <p className="font-outfit font-semibold text-[#0b1f4d] mb-4">Registration details</p>
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
      {[['Situation', s.registration.current_status], ['Education', s.registration.education], ['Experience', s.registration.experience],
        ['Career interest', s.registration.career_interest], ['Goal', s.registration.primary_goal], ['Biggest challenge', s.registration.biggest_challenge],
        ['Timeline', s.registration.timeline], ['City', s.city]].map(([k, v]) => (
        <div key={k}><dt className="text-slate-500 text-xs">{k}</dt><dd className="text-slate-800">{v || '—'}</dd></div>
      ))}
    </dl>
    <div className="mt-4 flex flex-wrap gap-2">
      {s.self_reported_skills.map((k) => <Pill key={k.skill} className="bg-amber-50 text-amber-700">{k.skill} · {k.evidence === 'demonstrated' ? 'demonstrated' : 'self-reported'}</Pill>)}
    </div>
  </Card>
);

const BookConsultation = ({ studentId, refresh }) => {
  const [busy, setBusy] = useState(false);
  const book = async () => {
    setBusy(true);
    try { await studentApi('/me/consultation/book', { method: 'POST', body: { student_id: studentId } }); toast.success('Consultation booked · ₹499 paid (simulated)'); await refresh(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="bg-[#0b1f4d] text-white border-0" data-testid="overview-book-consultation">
      <Compass className="w-5 h-5 text-teal-300 mb-3" />
      <p className="font-outfit text-xl font-semibold">Book your Career Clarity Consultation</p>
      <p className="text-slate-300 text-sm mt-2">A one-to-one session with an Elevate consultant to confirm your target roles and build your Personalized Career Summary.</p>
      <button onClick={book} disabled={busy} data-testid="book-consultation-btn"
        className="mt-5 bg-teal-400 hover:bg-teal-300 disabled:opacity-60 text-[#0b1f4d] font-semibold text-sm px-5 py-2.5 rounded-full transition-colors">
        {busy ? 'Booking…' : 'Book & pay ₹499'}
      </button>
      <p className="text-[11px] text-slate-400 mt-2">Prototype: payment is simulated.</p>
    </Card>
  );
};

const BookReal = () => (
  <Card className="bg-[#0b1f4d] text-white border-0" data-testid="overview-book-consultation">
    <Compass className="w-5 h-5 text-teal-300 mb-3" />
    <p className="font-outfit text-xl font-semibold">Book your Career Clarity Consultation</p>
    <p className="text-slate-300 text-sm mt-2">A one-to-one session with an Elevate consultant to confirm your target roles. Your personalised career action plan is included in the ₹499 fee.</p>
    <a href="/apply?plan=clarity-session" data-testid="book-consultation-btn"
      className="inline-block mt-5 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold text-sm px-5 py-2.5 rounded-full transition-colors">Book & pay ₹499</a>
  </Card>
);

const ReadinessCta = ({ link }) => (
  <Card data-testid="overview-readiness-cta">
    <p className="font-outfit font-semibold text-[#0b1f4d]">Take the free Career Readiness Check</p>
    <p className="text-sm text-slate-600 mt-2">12 quick questions, about 3 minutes. See where you stand on direction, profile, interviews and job search. Optional, but it makes your recommendations sharper.</p>
    <a href={link} className="inline-block mt-4 bg-[#0b1f4d] hover:bg-[#13296b] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors" data-testid="overview-take-readiness-btn">Start the Readiness Check</a>
  </Card>
);

const NextUp = ({ d, go, openMission, studentId, refresh }) => {
  if (d.consultation.status === 'not_booked') return d.is_demo ? <BookConsultation studentId={studentId} refresh={refresh} /> : <BookReal />;
  if (!d.summary) return (
    <Card className="bg-[#0b1f4d] text-white border-0" data-testid="overview-awaiting">
      <Clock3 className="w-5 h-5 text-teal-300 mb-3" />
      <p className="font-outfit text-xl font-semibold">Your consultation is {d.consultation.status === 'completed' ? 'complete' : 'booked'}.</p>
      <p className="text-slate-300 text-sm mt-2">Your Personalized Career Summary and program recommendations appear here once your consultant has reviewed and approved them.</p>
    </Card>
  );
  const rec = d.summary.primary;
  return (
    <Card className="bg-[#0b1f4d] text-white border-0" data-testid="overview-next">
      <Compass className="w-5 h-5 text-teal-300 mb-3" />
      {d.next_up.length ? (<>
        <p className="text-xs uppercase tracking-widest text-teal-300">Your next step</p>
        {d.next_up.map((n) => (
          <button key={n.mission_id} onClick={() => openMission(n.mission_id)} data-testid={`overview-next-${n.mission_id}`}
            className="mt-3 w-full flex items-center justify-between text-left bg-white/10 hover:bg-white/15 rounded-xl px-4 py-3 transition-colors">
            <span><span className="block font-semibold">{n.title}</span><span className="text-xs text-slate-300">{PROGRAM_NAMES[n.program_id]} · {STATE_META[n.state].label}</span></span>
            <ArrowRight className="w-4 h-4" />
          </button>))}
      </>) : (<>
        <p className="font-outfit text-xl font-semibold">Your summary is ready.</p>
        <p className="text-slate-300 text-sm mt-2">{rec ? `Recommended for you: ${PROGRAM_NAMES[rec]}.` : 'No paid program is needed right now.'}</p>
        <button onClick={() => go('summary')} className="mt-4 text-sm font-semibold text-teal-300" data-testid="overview-open-summary">Read my summary →</button>
      </>)}
      {d.coach_next_actions.length > 0 && (
        <div className="mt-5 border-t border-white/10 pt-4" data-testid="overview-coach-actions">
          <p className="text-xs uppercase tracking-widest text-teal-300 mb-2">From your coach</p>
          {d.coach_next_actions.map((a, i) => <p key={i} className="text-sm text-slate-200">• {a.text}</p>)}
        </div>)}
    </Card>
  );
};

const OpportunityAlert = ({ a, go }) => {
  if (!a || a.total_new < 1) return null;
  const roles = a.by_role.filter((r) => r.new_unseen > 0);
  return (
    <button onClick={() => go('opportunities')} data-testid="overview-opportunity-alert"
      className="lg:col-span-2 w-full flex items-center gap-4 text-left rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 hover:bg-amber-100 transition-colors">
      <span className="shrink-0 w-10 h-10 rounded-full bg-amber-400 text-[#0b1f4d] flex items-center justify-center"><BellRing className="w-5 h-5" /></span>
      <span className="flex-1">
        <span className="block font-outfit font-semibold text-[#0b1f4d]" data-testid="overview-opportunity-alert-count">{a.total_new} new opening{a.total_new === 1 ? '' : 's'} this week for your target roles</span>
        <span className="block text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{roles.map((r) => `${r.title} · ${r.new_unseen}`).join('  |  ')}</span>
      </span>
      <ArrowRight className="w-4 h-4 text-[#0b1f4d]" />
    </button>
  );
};

export const Overview = ({ data: d, go, openMission, studentId, refresh }) => (
  <>
    <SectionHeader eyebrow={d.student.persona_label} title={`Welcome, ${d.student.full_name.split(' ')[0]}`}>
      Where you stand → where you want to go → what to improve → how you're progressing.
    </SectionHeader>
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
      <OpportunityAlert a={d.opportunity_alerts} go={go} />
      <NextUp d={d} go={go} openMission={openMission} studentId={studentId} refresh={refresh} />
      {d.readiness_link ? <ReadinessCta link={d.readiness_link} /> : (
        <Card data-testid="overview-readiness">
          <p className="font-outfit font-semibold text-[#0b1f4d] mb-4">Career Readiness Check</p>
          <div className="space-y-4">{DIMENSIONS.map(([k, l]) => <ScoreBar key={k} label={l} value={(d.student.readiness || {})[k]} testid={`readiness-${k}`} />)}</div>
        </Card>)}
      <Card data-testid="overview-stages">
        <p className="font-outfit font-semibold text-[#0b1f4d] mb-4">Your journey</p>
        <ol className="space-y-3">
          {STAGES(d).map((st) => (
            <li key={st.t} className="flex items-start gap-3 text-sm">
              <CheckCircle2 className={`w-4 h-4 mt-0.5 ${st.done ? 'text-teal-600' : 'text-slate-300'}`} />
              <span className={st.done ? 'text-slate-800' : 'text-slate-500'}>{st.t}{st.note && <span className="block text-xs text-slate-500">{st.note}</span>}</span>
            </li>))}
        </ol>
      </Card>
      <TargetRolePicker key={d.student.id} data={d} studentId={studentId} refresh={refresh} />
      <Profile s={d.student} />
    </div>
  </>
);
